import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EfactoryPubSubService } from '@ats/service-pubsub';
import {
  EfpServiceUserNotificationService,
  NotificationProgressDirection,
  NotificationType
} from '@ats/service-user-notification';
import { TranslocoService } from '@ngneat/transloco';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap
} from 'rxjs';
import { IAppUser, UserRole } from '../../../interfaces/user.interface';
import { PMEUserService } from '../../services/pme-user.service';

export interface IEfpModuleContainer {
  container: string;
  displayname: string;
  description: string;
  flag: string;
  roles: string[];
  hidden?: boolean;
}

export interface IEfpModuleFromFlagsmith {
  module: string;
  displayname: string;
  description: string;
  mayaccess: boolean;
  link: string;
  icon: string;
  containers: IEfpModuleContainer[];
}

@Component({
  selector: 'app-masthead-drawer',
  templateUrl: './masthead-drawer.component.html',
  styleUrls: ['./masthead-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MastheadDrawerComponent implements OnInit, OnDestroy {
  constructor(
    private translate: TranslocoService,
    private ct: EfactoryPubSubService,
    private userNotification: EfpServiceUserNotificationService,
    private router: Router,
    private pmeUserService: PMEUserService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() public currentModule: string = '';
  @Input() public person: IAppUser | undefined;
  @Input() public buildNumber: string = '';

  /**
   * eFactoryPro host for module navigation
   */
  efpHost: string = '';
  /**
   * Whether Flagsmith has been initialized
   */
  flagsmithReady = false;
  /**
   * Whether Links list is shown
   */
  doShowLinks = false;
  /**
   * Uri for reports
   */
  reportsLink: string = '';
  /**
   * Current container for masthead label
   */
  currentContainer$ = new BehaviorSubject<IEfpModuleContainer | null>(null);
  /**
   * Used for styling the currently selected container
   */
  currentContainer: string = '';
  /**
   * Subscription termination subject
   */
  stopSubscriptions$ = new Subject<void>();
  /**
   * If form is shown for form protection
   */
  isFormShown = false;
  /**
   * PubSub subscription for form protection
   *
   * TODO: Needed?
   */
  formShown$!: Subscription;
  /**
   * Whether drawer is open
   */
  isOpen = false;
  /**
   * Currently selected module for "drill-in" view. When set, only this module and its containers
   * are shown in the drawer and a back button is displayed.
   */
  selectedModule: IEfpModuleFromFlagsmith = {
    module: 'Pme',
    displayname: 'Masthead.Pme',
    description: 'Masthead.PmeDescription',
    mayaccess: false,
    link: 'pme',
    icon: 'efpicon-masthead-pme',
    containers: [
      {
        container: 'parts',
        displayname: 'Masthead.PmeContainers.Parts',
        description: 'Masthead.PmeContainers.PartsDescription',
        flag: 'pme_parts',
        roles: [],
        hidden: false
      },
      {
        container: 'manufacturers',
        displayname: 'Masthead.PmeContainers.Manufacturers',
        description: 'Masthead.PmeContainers.ManufacturersDescription',
        flag: 'pme_manufacturers',
        roles: [],
        hidden: false
      },
      {
        container: 'nouns',
        displayname: 'Masthead.PmeContainers.Nouns',
        description: 'Masthead.PmeContainers.NounsDescription',
        flag: 'pme_nouns',
        roles: [],
        hidden: false
      },
      {
        container: 'requests',
        displayname: 'Masthead.PmeContainers.Requests',
        description: 'Masthead.PmeContainers.RequestsDescription',
        flag: 'pme_requests',
        roles: [],
        hidden: false
      },
      {
        container: 'imports',
        displayname: 'Masthead.PmeContainers.Imports',
        description: 'Masthead.PmeContainers.ImportsDescription',
        flag: 'pme_imports',
        roles: [],
        hidden: true
      }
    ]
  };
  /**
   * Browser language
   */
  lang: string = '';
  /**
   * Whether to show the R360 Support Request link
   */
  showR360Link = false;
  /**
   * Whether to show the Maintenance Forum link
   */
  showMaintenanceForumLink = true;

  ngOnInit(): void {
    this.lang = this.translate.getDefaultLang().substring(0, 2);
    const thisHost = this.getHostName();
    this.efpHost = thisHost.substring(thisHost.indexOf('.'));
    this.setReportsLink();
    this.startSubscriptions();

    this.pmeUserService.currentUserChanged$
      .pipe(
        takeUntil(this.stopSubscriptions$),
        tap(() => {
          for (const container of this.selectedModule.containers) {
            if (container.container === 'imports') {
              container.hidden = !this.pmeUserService.currentUser?.roles.includes(
                UserRole.PMEDataIntegrity
              );
            }
          }

          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.stopSubscriptions$.next();
    this.stopSubscriptions$.complete();
    this.currentContainer$.complete();
  }

  startSubscriptions(): void {
    const activePaths = window.location.pathname.split('/').filter((p) => p !== '');

    if (activePaths.length > 0) {
      this.emitCurrentContainer(activePaths[0]);
    }

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e: NavigationEnd) => {
          const urlParts = e.url.split('/');

          if (urlParts.length > 1) {
            const currentRoute = urlParts[1];

            this.emitCurrentContainer(currentRoute);
          }
        }),
        takeUntil(this.stopSubscriptions$)
      )
      .subscribe();

    this.ct
      .$sub('siteSelection:formShown')
      .pipe(takeUntil(this.stopSubscriptions$))
      .subscribe((payload: boolean) => {
        this.isFormShown = payload as boolean;
        this.cdr.markForCheck();
      });
  }

  /**
   * Sets the reports link property for menu items
   */
  setReportsLink(): void {
    const thisHost = this.getHostName();

    this.efpHost = thisHost.substring(thisHost.indexOf('.'));

    if (thisHost.indexOf('dev') > -1 || thisHost === 'me') {
      this.reportsLink = 'https://ssrs.dev.advancedtech.com/Reports/browse/';

      if (thisHost === 'me') {
        this.efpHost = '.example.com';
      }
    } else if (thisHost.indexOf('qa') > -1) {
      this.reportsLink = 'https://ssrs.qa.advancedtech.com/Reports/browse/';
    } else {
      this.reportsLink = 'https://ssrs.advancedtech.com/Reports/browse/';
    }
  }

  emitCurrentContainer(container: string): void {
    if (!this.selectedModule) {
      return;
    }

    const currentContainer = this.selectedModule.containers.find((c) => c.container === container);

    // Container not found in the current module, exit quietly.
    if (!currentContainer) {
      return;
    }

    this.currentContainer$.next(currentContainer);
    this.currentContainer = `${this.selectedModule.module}|${currentContainer.container}`;
    this.cdr.markForCheck();
  }

  /**
   * Updates masthead modules and containers based on access guards
   */
  updateMasthead(): void {
    if (!this.person) {
      return;
    }

    const activePaths = window.location.pathname.split('/').filter((p) => p !== '');

    if (activePaths.length > 0) {
      this.emitCurrentContainer(activePaths[0]);
    }
  }

  /**
   * Checks a container for required flag, required roles, and disallowed roles. Used as an array filter.
   * @param container container being checked
   * @returns `true` if all conditions are met, otherwise `false`.
   */
  processContainer(container: IEfpModuleContainer): boolean {
    if (!this.person) {
      return false;
    }

    // Disallowed Roles begin with a hyphen - filter those and remove the hyphen
    const disallowedRoles = container.roles
      .filter((r) => r.startsWith('-'))
      .map((r) => r.substring(1));

    // Required roles are those without a hyphen
    const requiredRoles = container.roles.filter((r) => !r.startsWith('-'));

    // If user has any disallowed roles then return false.
    if (
      disallowedRoles.length &&
      this.person.roles
        .map((r: string) => r?.toUpperCase())
        .some((r) => disallowedRoles.includes(r))
    ) {
      return false;
    }

    // If user has none of the required roles then return false.
    if (
      requiredRoles.length &&
      !this.person.roles.map((r: string) => r?.toUpperCase()).some((r) => requiredRoles.includes(r))
    ) {
      return false;
    }

    // All checks pass so return true to include the container.
    return true;
  }

  /**
   * Navigates to a different container within the current module or navigates to the new module
   * @param link module subdomain
   * @param container container name for routing
   */
  routeToContainer(link: string, container: string): void {
    if (this.selectedModule.link === link) {
      if (this.selectedModule.containers.find((c) => c.container === container)) {
        this.protectForm()
          .pipe(
            take(1),
            filter((value) => value),
            tap(() => {
              this.router.navigate([container]);
              this.isOpen = false;
              this.cdr.markForCheck();
            })
          )
          .subscribe();
      }

      return;
    }
  }

  /**
   * Routes to root of current module
   */
  routeToRoot(): void {
    this.protectForm()
      .pipe(
        take(1),
        filter((value) => value),
        tap(() => {
          this.router.navigate(['/']);
          this.isOpen = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  /**
   * Simulates a click on a hidden link
   * @param link ID of hidden link
   */
  gotoLink(link: string): void {
    document.getElementById(link)?.click();
  }

  // Function to aid unit testing
  getHostName(): string {
    return window.location.hostname;
  }

  /**
   * Displays toast message with action buttons to protect a displayed form
   * @returns boolean if user wants to continue
   */
  protectForm(): Observable<boolean> {
    if (this.isFormShown) {
      return this.userNotification
        .showObservable({
          type: NotificationType.Warning,
          // eslint-disable-next-line max-len
          text: this.translate.translate(
            `Attention: If you have any unsaved changes, they will be lost. Are you sure that you'd like to continue?`
          ) as string,
          action1: true,
          action1Text: this.translate.translate('Continue') as string,
          action1OnDefault: false,
          action2: true,
          action2Text: this.translate.translate('Stay Here') as string,
          action2OnDefault: true,
          progressBar: true,
          progressAnimation: NotificationProgressDirection.Increasing
        })
        .pipe(map((value: { action: boolean }) => value.action));
    }

    return of(true);
  }
}
